import DefaultFontStyles from '@devsisters/gatsby-theme-emotion-webfonts/src/components/DefaultFontStyle';
import { Global } from '@emotion/react';
import * as React from 'react';

import Fonts from '../components/Fonts';
import Seo from '../components/Seo';
import { getTranslationText, l10nContext } from '../utils/l10nContext';

import type { PageProps } from 'gatsby';
import type { LanguageType } from '../utils/l10nContext';

type LayoutProps = PageProps<
  GatsbyTypes.MainPageQuery,
  {
    language: string;
    titleImagePath: string;
    buttonImagePath: string;
    pathname: string;
    game: string;
    backgroundColor?: string;
  }
>;

const Layout: React.FC<LayoutProps> = ({ data, children, pageContext }) => {
  const lang = pageContext?.language ?? 'en';
  const backgroundColor = pageContext?.backgroundColor ?? '#3a3633';
  type L10nContext = React.ContextType<typeof l10nContext>;
  const l10n = React.useMemo<L10nContext>(() => {
    if (!data?.pageTarget?.translations) {
      return null;
    }
    const language = data.pageTarget.language as LanguageType;
    const messages = Object.fromEntries(data.pageTarget.translations.entries.map(({ key, value }) => [key, value]));
    if (!(language && messages)) {
      return null;
    }
    return {
      language,
      messages,
      t: (key, components) => getTranslationText({ language, messages }, key, components),
    };
  }, [data]);
  const gameCode = data?.pageTarget?.pathname.includes('kingdom') ? 'ck' : 'cro';
  const game = data?.pageTarget?.game;
  const t = React.useCallback<NonNullable<L10nContext>['t']>(
    (key) => {
      if (!l10n) {
        window.location.href = '/';
        return 'Localization data not found';
      }
      return l10n.t(key);
    },
    [l10n],
  );
  return (
    <l10nContext.Provider value={l10n}>
      <Global
        styles={{
          '*': {
            fontFamily: 'inherit',
            boxSizing: 'border-box',
            margin: 0,
            padding: 0,
          },
          html: {
            background: backgroundColor,
          },
          a: {
            textDecoration: 'none',
            color: 'inherit',
          },
        }}
      />
      <Fonts
        notoSans={lang === 'en' || lang === 'de'}
        notoSansJp={lang === 'ja'}
        notoSansKr={lang === 'ko'}
        notoSansTc={lang === 'zh-Hant'}
        notoSansTh={lang === 'th'}
        cookieRun={lang === 'en' || lang === 'ko' || (lang === 'fr' && game === 'cwc')}
      />
      <DefaultFontStyles />
      {pageContext.language && (
        <Seo
          lang={lang}
          title={t('devplayguide_meta_title') as string}
          description={
            (gameCode
              ? t(`devplayguide_meta_description_${pageContext.game}-${gameCode}`)
              : t('devplayguide_meta_title')) as string
          }
          game={pageContext.game}
        />
      )}
      {children}
    </l10nContext.Provider>
  );
};

export default Layout;
